import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import CategoryBadge from "@/components/feature/CategoryBadge";
import Button from "@/components/base/Button";
import Modal from "@/components/base/Modal";
import ConfirmDialog from "@/components/feature/ConfirmDialog";
import ExpenseForm from "@/components/feature/ExpenseForm";
import EmptyState from "@/components/feature/EmptyState";
import { useToast } from "@/store/ToastContext";
import { formatCurrency } from "@/utils/currency";
import { formatDate, formatTime } from "@/utils/date";
import { paymentLabel, paymentIcon, formatFileSize } from "@/utils/format";
import { expenseApi, categoryApi, settingsApi } from "@/services/api";
import type { Expense, Category, AppSettings } from "@/models/types";

export default function ExpenseDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [expense, setExpense] = useState<Expense | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const [expenseData, categoriesData, settingsData] = await Promise.all([
          expenseApi.getById(id),
          categoryApi.list(),
          settingsApi.get(),
        ]);
        setExpense(expenseData);
        setCategories(categoriesData);
        setSettings(settingsData);
      } catch (error) {
        console.error("Error cargando detalle del gasto:", error);
        showToast("Error al cargar el gasto.", "error");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, showToast]);

  const handleDelete = async () => {
    if (!expense) return;
    setDeleting(true);
    try {
      await expenseApi.remove(expense.id);
      showToast("Gasto eliminado correctamente.");
      navigate("/gastos");
    } catch (error) {
      console.error("Error eliminando gasto:", error);
      showToast("Error al eliminar el gasto.", "error");
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
        </div>
      </AppLayout>
    );
  }

  if (!expense || !settings) {
    return (
      <AppLayout>
        <EmptyState
          icon="ri-error-warning-line"
          title="Gasto no encontrado"
          description="Este gasto ya no existe o fue eliminado."
          action={
            <Link
              to="/gastos"
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 text-white px-4 py-2.5 text-sm font-semibold hover:bg-primary-600 cursor-pointer"
            >
              Volver a gastos
            </Link>
          }
        />
      </AppLayout>
    );
  }

  const category = categories.find((c) => c.id === expense.categoryId);

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto">
        <Link
          to="/gastos"
          className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-900 mb-4 cursor-pointer"
        >
          <i className="ri-arrow-left-line" /> Volver a gastos
        </Link>

        {/* Importe destacado */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-6 mb-4">
          <p className="text-sm text-foreground-500">Importe</p>
          <p className="mt-1 text-4xl font-heading font-bold text-foreground-950">
            {formatCurrency(expense.amount, settings.currency)}
          </p>
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <CategoryBadge categoryId={expense.categoryId} size="md" />
            <span className="inline-flex items-center gap-1.5 text-sm text-foreground-600">
              <i className={paymentIcon(expense.paymentMethod)} />
              {paymentLabel(expense.paymentMethod)}
            </span>
          </div>
        </div>

        {/* Detalles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-background-50 border border-background-200 rounded-lg p-5 space-y-4">
            <DetailRow label="Descripción" value={expense.description} />
            <DetailRow label="Fecha" value={formatDate(expense.date)} />
            {expense.createdAt && (
              <DetailRow
                label="Fecha de creación"
                value={`${formatDate(expense.createdAt)} · ${formatTime(expense.createdAt)}`}
              />
            )}
            <DetailRow
              label="Categoría"
              value={category?.name ?? "—"}
              icon={category?.icon}
              iconColor={category?.color}
            />
          </div>
          <div className="bg-background-50 border border-background-200 rounded-lg p-5">
            <p className="text-xs font-medium text-foreground-500 uppercase tracking-wide mb-2">
              Notas
            </p>
            <p className="text-sm text-foreground-800 whitespace-pre-wrap">
              {expense.notes || "Sin notas."}
            </p>
          </div>
        </div>

        {/* Recibo */}
        {expense.receipt && (
          <div className="bg-background-50 border border-background-200 rounded-lg p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground-900">Recibo</h3>
              <a
                href={expense.receipt.url}
                download={expense.receipt.fileName}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                <i className="ri-download-line" /> Descargar
              </a>
            </div>
            {expense.receipt.fileType === "image" ? (
              <img
                src={expense.receipt.url}
                alt={expense.receipt.fileName}
                className="w-full max-w-sm rounded-md border border-background-200 object-cover object-top"
              />
            ) : (
              <div className="flex items-center gap-3 p-4 rounded-md bg-background-100">
                <span className="w-10 h-10 rounded-md bg-red-100 text-red-500 flex items-center justify-center">
                  <i className="ri-file-pdf-2-line text-2xl" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">
                    {expense.receipt.fileName}
                  </p>
                  <p className="text-xs text-foreground-500">
                    {formatFileSize(expense.receipt.fileSize)}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Acciones */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button icon="ri-edit-line" onClick={() => setEditOpen(true)}>
            Editar gasto
          </Button>
          <Button
            variant="danger"
            icon="ri-delete-bin-line"
            onClick={() => setDeleteOpen(true)}
            disabled={deleting}
          >
            Eliminar gasto
          </Button>
        </div>
      </div>

      {/* Modal edición */}
      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Editar gasto"
        maxWidth="max-w-2xl"
      >
        <div className="p-5">
          <ExpenseForm
            initial={expense}
            categories={categories}
            onSaved={() => {
              setEditOpen(false);
              // Recargar el gasto actualizado
              expenseApi.getById(expense.id).then(setExpense).catch(console.error);
            }}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={deleteOpen}
        title="Eliminar gasto"
        message={`¿Seguro que quieres eliminar "${expense.description}"? Esta acción no se puede deshacer.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </AppLayout>
  );
}

function DetailRow({
  label,
  value,
  icon,
  iconColor,
}: {
  label: string;
  value: string;
  icon?: string;
  iconColor?: string;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-foreground-500 uppercase tracking-wide">
        {label}
      </p>
      <p className="mt-1 text-sm font-medium text-foreground-900 flex items-center gap-2">
        {icon && <i className={icon} style={iconColor ? { color: iconColor } : undefined} />}
        {value}
      </p>
    </div>
  );
}